// @ts-nocheck
import gql from 'graphql-tag';
import { query as customerAddresses } from '$lib/utils/shopify/queries/customerAddresses';
import { redirect } from '@sveltejs/kit';

export const createCustomerClient = (providedFetch = fetch) => {
    const customerAccountApiUrl = `/api/customer`;

    async function fetchCustomerAPI({ query, variables = {} }) {
        const response = await providedFetch(customerAccountApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query, variables })
        });

        const body = await response.text();

        if (!response.ok) {
            if (response.status === 401) {
                // Auth token expired/invalid, redirect through login flow
                const currentPath = window.location.pathname;
                redirect(302, `/api/auth/login?redirectTo=${encodeURI(currentPath)}`);
            }

            let errors;
            try {
                errors = JSON.parse(body);
            } catch (_e) {
                errors = [{ message: body }];
            }
            return { errors };
        } else {
            try {
                const APIresponse = JSON.parse(body);
                const { errors } = APIresponse;

                const gqlErrors = errors?.map(({ message, ...rest }) => ({
                    message,
                    request: {
                        ...rest,
                        requestId: response.headers.get('x-request-id'),
                        queryVariables: variables,
                        query
                    }
                }));
                return { ...APIresponse, ...(errors && { errors: gqlErrors }) };
            } catch (e) {
                return { errors: [{ message: body }] };
            }
        }
    }

    return {
        query: ({ query, variables }) => fetchCustomerAPI({ query, type: 'query', variables }),
        mutate: ({ mutation, variables }) => fetchCustomerAPI({ query: mutation, type: 'mutation', variables })
    };
};

const _privateCustomerClientInstance = createCustomerClient();

// set default shipping address
export const setDefaultAddress = async (addressId) => {
    const setDefaultAddressMutation = gql`
        mutation customerDefaultAddressUpdate($addressId: ID!) {
            customerAddressUpdate(addressId: $addressId, defaultAddress: true) {
                customerAddress {
                    id
                }
                customerUserErrors: userErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
    return _privateCustomerClientInstance
        .mutate({
            mutation: setDefaultAddressMutation,
            variables: { addressId }
        })
        .then((update) => {
            if (update?.data?.customerAddressUpdate?.customerUserErrors?.length) return update;
            return getCustomerAddresses();
        });
};

// get all customer addresses
export const getCustomerAddresses = async () =>
    _privateCustomerClientInstance.query({
        query: customerAddresses
    });

// get single customer address by ID
export const getCustomerAddressById = async (addressId) => {
    const addressFetch = await getCustomerAddresses();
    return addressFetch?.data?.customer?.addresses?.edges?.find((address) => address?.node?.id === addressId);
};

// delete shippping address
export const deleteAddress = async (addressId) => {
    const deleteAddressMutation = gql`
        mutation customerAddressDelete($addressId: ID!) {
            customerAddressDelete(addressId: $addressId) {
                customerUserErrors: userErrors {
                    code
                    field
                    message
                }
                deletedAddressId
            }
        }
    `;
    return _privateCustomerClientInstance
        .mutate({
            mutation: deleteAddressMutation,
            variables: { addressId }
        })
        .then((deletedAddress) => {
            if (deletedAddress?.data?.customerAddressDelete?.customerUserErrors?.length) return deletedAddress;
            return getCustomerAddresses().then(({ data }) => {
                return data?.customer;
            });
        });
};

// add new shipping address
export const addNewAddress = (address) => {
    const addNewAddressMutation = gql`
        mutation customerAddressCreate($address: CustomerAddressInput!) {
            customerAddressCreate(address: $address) {
                customerAddress {
                    id
                }
                customerUserErrors: userErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
    return _privateCustomerClientInstance.mutate({
        mutation: addNewAddressMutation,
        variables: { address }
    });
};

// update existing address
export const updateAddress = (address, addressId) => {
    const updateAddressMutation = gql`
        mutation customerAddressUpdate($addressId: ID!, $address: CustomerAddressInput) {
            customerAddressUpdate(addressId: $addressId, address: $address) {
                customerAddress {
                    id
                }
                customerUserErrors: userErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
    return _privateCustomerClientInstance.mutate({
        mutation: updateAddressMutation,
        variables: { address, addressId }
    });
};
